import { queryView, type QueryResultLike } from '@krp/client-runtime'

import type { ProductRef } from '../../../shared/product'
import type { CommonCatalogPage } from '../../catalog/model/types'
import { commonPhotoThumbUrl } from '../../common-photos/api/urls'

/** Сколько товаров общего каталога показывать по одному штрихкоду: больше — уже не «этот товар». */
export const SCANNED_PRODUCTS_LIMIT = 10

/** Повтор того же кода, после которого код без контрольной цифры считается прочитанным, а не шумом кадра. */
export const BARCODE_CONFIRM_MS = 1500

/** Товарный код GTIN (EAN-8, UPC-A, EAN-13, ITF-14) со сходящейся контрольной цифрой. */
export function isValidGtin(code: string): boolean {
  if (!/^(\d{8}|\d{12,14})$/.test(code)) return false
  const digits = [...code].map(Number)
  const check = digits.pop()
  const sum = digits.reverse().reduce((total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1), 0)
  return (10 - (sum % 10)) % 10 === check
}

export interface BarcodeCandidate {
  code: string
  at: number
}

/**
 * Принять ли код с потока камеры (RP-FB4). Камера на фактуре (клавиатура, ткань) иногда «читает» короткие ЦИФРЫ —
 * такой код уходил в каталог и показывал случайные товары. Код с верной контрольной цифрой GTIN принимается сразу;
 * код с не-цифрами (QR со ссылкой, Code 128 с буквами) — тоже сразу: такую строку шум фактуры не даёт, а ждать второго
 * чтения значило не показывать «Код считан» в момент наведения (RP-FB58). Остальные цифровые коды — только когда тот же
 * код прочитан ещё раз подряд за {@link BARCODE_CONFIRM_MS}.
 */
export function acceptsBarcode(previous: BarcodeCandidate | null, code: string, now: number): boolean {
  if (isValidGtin(code) || !/^\d+$/.test(code)) return true
  return previous !== null && previous.code === code && now - previous.at <= BARCODE_CONFIRM_MS
}

/**
 * Окно подавления дребезга кадров (RP-FB4, RP-FB58): сколько код должен пробыть вне кадра, чтобы повторное наведение на
 * него считалось НОВЫМ сканированием. Единственная настройка: короче — дребезг одного кадра даёт лишние срабатывания,
 * длиннее — быстрое «убрал и навёл снова» не считалось (было 1800 мс, и повтор молча проглатывался).
 */
export const CODE_ABSENCE_MS = 1000

/**
 * Новое ли это сканирование. Камера выдаёт один и тот же код кадр за кадром, пока он в кадре, — это не новое.
 * Новое: другой код, либо тот же, но он уходил из кадра на {@link CODE_ABSENCE_MS} и больше. `previous.at` — время
 * ПОСЛЕДНЕГО чтения (не первого): пока код в кадре, оно обновляется, и «отсутствие» меряется от последнего кадра с ним.
 */
export function isNewScan(previous: BarcodeCandidate | null, code: string, now: number): boolean {
  return previous === null || previous.code !== code || now - previous.at >= CODE_ABSENCE_MS
}

/** Последнее сканирование окна: код и порядковый номер — новый номер заново открывает лист, даже если код тот же. */
export interface ScanState {
  code: string
  id: number
}

export const NO_SCAN: ScanState = { code: '', id: 0 }

/** Код с камеры принят как новое сканирование. */
export function scanned(state: ScanState, code: string): ScanState {
  return { code, id: state.id + 1 }
}

/** Снимок или сброс: кода с камеры больше нет, номер растёт — прежний лист не должен «воскреснуть». */
export function scanCleared(state: ScanState): ScanState {
  return { code: '', id: state.id + 1 }
}

export interface ScannedProductVm {
  id: string
  name: string
  sku: string | null
  /** Где есть товар: имена ресурсов через запятую; пусто — нигде из доступных вам. */
  sources: string
  /** Карточки товара в источниках: запасное фото, когда у общего товара своих фото нет. */
  refs: readonly ProductRef[]
  /** Миниатюра главного фото ОБЩЕГО товара (адрес у Platform); `null` — своих фото у товара нет. */
  commonPhotoUrl: string | null
}

/** Что дал прочитанный штрихкод (RP-FB4): ищем, нашли, нет в каталоге или поиск не удался. */
export type BarcodeLookupVm =
  | { kind: 'none' }
  | { kind: 'searching'; code: string }
  | { kind: 'found'; code: string; products: readonly ScannedProductVm[] }
  | { kind: 'missing'; code: string }
  | { kind: 'failed'; code: string; message: string }

const NONE: BarcodeLookupVm = { kind: 'none' }

/**
 * Итог штрихкода по ответу общего каталога. Результат — для ТЕКУЩЕГО кода: binding передаёт `currentData`, иначе
 * при новом коде на экране остался бы ответ на прежний, как будто он про новый.
 */
export function toBarcodeLookup(code: string, result: QueryResultLike<CommonCatalogPage>): BarcodeLookupVm {
  if (code === '') return NONE
  const view = queryView(result, (page) => page.items.length === 0)
  switch (view.status) {
    case 'idle':
    case 'loading':
      return { kind: 'searching', code }
    case 'empty':
      return { kind: 'missing', code }
    case 'ready':
      return {
        kind: 'found',
        code,
        products: (result.data?.items ?? []).map((item) => ({
          id: item.id,
          name: item.displayName,
          sku: item.sku,
          sources: item.identities.map((identity) => identity.resourceName).join(', '),
          refs: item.identities.map((identity) => ({
            resourceId: identity.resourceId,
            resourceKind: identity.resourceKind,
            remoteKey: identity.remoteKey,
            state: null,
          })),
          commonPhotoUrl: commonPhotoThumbUrl(item.id, item.mainPhoto),
        })),
      }
    default:
      return { kind: 'failed', code, message: view.error?.message ?? 'Каталог не ответил' }
  }
}
